/**
 * src/services/spatial/spatialContractExecution.ts
 *
 * Host-side bridge that signs + broadcasts the Soroban transaction GeoLink
 * authorises for a spatial-object interaction.
 *
 * Flow (driven by SpatialObjectInteractionPanel):
 *   1. The panel records the interaction and calls /execute, which returns
 *      an AUTHORISED envelope: { contractAddress, functionName,
 *      functionParameters, actorWallet }. The panel shows that envelope
 *      to the user — see `describeParams` — and waits to be told to go on.
 *   2. We build a Soroban invoke for that contract function, SIMULATE it
 *      (this is the safety gate — a bad/incorrectly-typed call fails here
 *      and is NEVER broadcast), prepare (fills auth + footprint), sign with
 *      the connected wallet, submit, and poll for inclusion.
 *
 * GeoLink's spatial /execute does not return a ready-to-sign XDR (unlike the
 * pending-deposits prepare endpoint), so we assemble the invocation here.
 * Because `functionParameters` is an object we encode values heuristically
 * and pass them positionally in key order; the simulate step guarantees we
 * never broadcast a malformed call. If a GeoLink build ever DOES include an
 * unsigned XDR on the execution envelope we sign that directly instead.
 */

import {
  Address,
  BASE_FEE,
  Contract,
  Networks,
  nativeToScVal,
  rpc,
  TransactionBuilder,
  xdr,
} from '@stellar/stellar-sdk';
import type { Wallet } from '../../wallet';

/** Build an rpc.Server pointed at the same endpoint the rest of the app uses. */
function getRpcServer(): rpc.Server {
  const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
  const isDevelopment =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const finalRpcUrl = isDevelopment
    ? `${window.location.protocol}//${window.location.hostname}:8080/api/soroban-rpc`
    : rpcUrl;
  const opts: { allowHttp?: boolean } = {};
  if (isDevelopment && finalRpcUrl.startsWith('http://')) opts.allowHttp = true;
  return new rpc.Server(finalRpcUrl, opts);
}

const STELLAR_ADDRESS_RE = /^[GC][A-Z2-7]{55}$/;

/**
 * What a parameter was encoded as.
 *
 * Soroban needs typed values and we have no contract spec here, so the
 * type is inferred. `unencodable` is the SDK refusing the value, which
 * is worth saying out loud rather than discovering at signing time.
 */
export type ParamEncoding =
  | 'Address'
  | 'i128'
  | 'string'
  | 'bool'
  | 'void'
  | 'number'
  | 'structured'
  | 'unencodable';

/**
 * Best-effort JS value → ScVal encoder, and the name of what it decided.
 *
 * Both come back together on purpose. The user is shown this
 * classification before signing, and a second function that merely
 * agreed with this one would be free to drift from it — so there is no
 * second function. What the panel displays and what the transaction
 * carries are the same decision, read twice.
 *
 * The inference: G…/C… strings → Address, integer-ish → i128 (the
 * common amount type), everything else → nativeToScVal's default
 * mapping. A string that looks like an address but fails to decode
 * falls through to the later branches, which is why the classification
 * cannot be computed without attempting the encoding.
 *
 * The simulate gate catches any mistype before broadcast.
 */
function encodeParamWithEncoding(value: unknown): { scVal: xdr.ScVal; encodedAs: ParamEncoding } {
  if (value === null || value === undefined) {
    return { scVal: xdr.ScVal.scvVoid(), encodedAs: 'void' };
  }

  if (typeof value === 'string') {
    if (STELLAR_ADDRESS_RE.test(value)) {
      try {
        return { scVal: new Address(value).toScVal(), encodedAs: 'Address' };
      } catch { /* fall through */ }
    }
    if (/^-?\d+$/.test(value)) {
      try {
        return { scVal: nativeToScVal(BigInt(value), { type: 'i128' }), encodedAs: 'i128' };
      } catch { /* fall through */ }
    }
    return { scVal: nativeToScVal(value), encodedAs: 'string' };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? { scVal: nativeToScVal(value, { type: 'i128' }), encodedAs: 'i128' }
      : { scVal: nativeToScVal(value), encodedAs: 'number' };
  }
  if (typeof value === 'bigint') {
    return { scVal: nativeToScVal(value, { type: 'i128' }), encodedAs: 'i128' };
  }
  if (typeof value === 'boolean') {
    return { scVal: nativeToScVal(value), encodedAs: 'bool' };
  }

  // Objects / arrays — let the SDK do its best (maps / vecs).
  return { scVal: nativeToScVal(value as never), encodedAs: 'structured' };
}

function encodeParam(value: unknown): xdr.ScVal {
  return encodeParamWithEncoding(value).scVal;
}

/** One authorized parameter, as it will be sent and as it will be shown. */
export interface ParamDisclosure {
  /** The key GeoLink returned. Positional order, not a contract arg name. */
  key: string;
  /** Verbatim. Never reformatted, never scaled, never given a unit. */
  value: unknown;
  encodedAs: ParamEncoding;
}

/**
 * Describe an authorized parameter bag for a user about to sign it.
 *
 * Key order is argument order: `executeSpatialContractCall` passes
 * `Object.values(...)` positionally, so the rows are numbered the way
 * the contract will receive them.
 *
 * Deliberately not interpreted. There is no asset, amount, recipient or
 * fee field on GeoLink's authorized execution — only this untyped bag —
 * so a row says "i128: 10000000", never "10 XLM". Guessing a denomination
 * we cannot know would put a confident wrong number in front of someone
 * about to approve a transfer.
 */
export function describeParams(parameters: Record<string, unknown> | null | undefined): ParamDisclosure[] {
  const bag = parameters ?? {};
  return Object.keys(bag).map((key) => {
    const value = bag[key];
    try {
      return { key, value, encodedAs: encodeParamWithEncoding(value).encodedAs };
    } catch {
      // The SDK refused it. Signing would fail on this value too, so
      // say which one rather than rendering a type it will never have.
      return { key, value, encodedAs: 'unencodable' as const };
    }
  });
}

export interface SpatialExecutionEnvelope {
  contractAddress: string;
  functionName: string;
  functionParameters: Record<string, unknown>;
  actorWallet: string;
  /** Some GeoLink builds may attach a prepared, unsigned XDR — preferred. */
  unsignedXdr?: string | null;
}

export interface SpatialExecutionResult {
  txHash: string;
}

/**
 * Sign + broadcast the authorised Soroban call. Returns the tx hash, or
 * throws with a human-readable message (which the panel surfaces and PATCHes
 * back to GeoLink as a `failed` execution).
 */
export async function executeSpatialContractCall(
  wallet: Wallet,
  envelope: SpatialExecutionEnvelope,
  network: string = Networks.TESTNET,
): Promise<SpatialExecutionResult> {
  const server = getRpcServer();

  // Preferred path: GeoLink already prepared the transaction → just sign it.
  if (envelope.unsignedXdr && envelope.unsignedXdr.trim()) {
    const signedXdr = await wallet.signTransaction(envelope.unsignedXdr.trim(), network);
    const signedTx = TransactionBuilder.fromXDR(signedXdr, network);
    const sent = await server.sendTransaction(signedTx);
    if (sent.status === 'ERROR') {
      throw new Error(`Submit failed: ${JSON.stringify(sent.errorResult)}`);
    }
    return pollForInclusion(server, sent.hash);
  }

  // Assemble the invocation ourselves.
  if (!envelope.contractAddress || !envelope.functionName) {
    throw new Error('GeoLink did not return a contract address / function to execute.');
  }

  let sourceAccount;
  try {
    sourceAccount = await server.getAccount(envelope.actorWallet);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes('not found') || msg.includes('Account not found')) {
      throw new Error('Your account is not funded on the network yet. Fund it and try again.');
    }
    throw err;
  }

  const contract = new Contract(envelope.contractAddress);
  const args = Object.values(envelope.functionParameters ?? {}).map(encodeParam);

  const tx = new TransactionBuilder(sourceAccount, {
    fee: BASE_FEE,
    networkPassphrase: network,
  })
    .addOperation(contract.call(envelope.functionName, ...args))
    .setTimeout(120)
    .build();

  // ── Safety gate: simulate before anything is signed/broadcast ──
  const sim = await server.simulateTransaction(tx);
  if ('error' in sim && sim.error) {
    throw new Error(`Simulation failed: ${typeof sim.error === 'string' ? sim.error : JSON.stringify(sim.error)}`);
  }

  const prepared = await server.prepareTransaction(tx);
  const signedXdr = await wallet.signTransaction(prepared.toXDR(), network);
  const signedTx = TransactionBuilder.fromXDR(signedXdr, network);

  const sent = await server.sendTransaction(signedTx);
  if (sent.status === 'ERROR') {
    throw new Error(`Submit failed: ${JSON.stringify(sent.errorResult)}`);
  }
  return pollForInclusion(server, sent.hash);
}

/** Poll the RPC until the tx is included (or we give up after ~30 ledgers). */
async function pollForInclusion(server: rpc.Server, hash: string): Promise<SpatialExecutionResult> {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    await new Promise((r) => setTimeout(r, 1000));
    try {
      const res = await server.getTransaction(hash);
      if (res.status === rpc.Api.GetTransactionStatus.SUCCESS) return { txHash: hash };
      if (res.status === rpc.Api.GetTransactionStatus.FAILED) {
        throw new Error('Transaction failed on-chain.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('not found') || msg.includes('not in ledger')) continue;
      throw err;
    }
  }
  // Submitted but not yet confirmed — return the hash so the audit trail
  // still records it; the explorer link lets the user verify.
  return { txHash: hash };
}
